import { Tooltip } from 'antd';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import type { ProtectionGroup, ProtectionUser } from '../../../interfaces/AccessMap';
import { Icon } from '../../Core/Icon';

interface Props {
  users: ProtectionUser[];
  groups: ProtectionGroup[];
  emptyText?: string;
  compact?: boolean;
  className?: string;
}

const userLabel = (u: ProtectionUser): string => {
  const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return fullName || u.userName || `#${u.id}`;
};

export const RecipientsList = ({ users, groups, emptyText, compact = false, className }: Props) => {
  const { t } = useTranslation();

  if (users.length === 0 && groups.length === 0) {
    return (
      <p className={clsx('text-blue-dark/70 text-sm', className)}>
        {emptyText ?? t('common.noRecipients')}
      </p>
    );
  }

  const chipClass = clsx(
    'bg-azure-light text-blue-dark dark:bg-gray-light flex flex-row items-center gap-1.5 rounded-full',
    compact ? 'px-2 py-0.5' : 'px-2.5 py-1',
  );
  const iconSize = compact ? 'h-3.5 w-3.5' : 'h-4 w-4';
  const textSize = compact ? 'text-xs' : 'text-sm';

  return (
    <div className={clsx('flex flex-row flex-wrap gap-1.5', className)}>
      {groups.map((g) => (
        <Tooltip
          key={`g-${g.id}`}
          title={`${t('common.group')} • ${
            g.members ? t('common.numberOfMembersInline', { count: g.members }) : g.name
          }`}
        >
          <div className={chipClass}>
            <Icon name="UsersIcon" className={clsx('text-azure shrink-0', iconSize)} />
            <span className={clsx('max-w-40 truncate font-medium', textSize)}>{g.name}</span>
            {g.members ? (
              <span className={clsx('text-blue-dark/60', textSize)}>· {g.members}</span>
            ) : null}
          </div>
        </Tooltip>
      ))}
      {users.map((u) => (
        <Tooltip key={`u-${u.id}`} title={`${t('common.user')} • ${userLabel(u)}`}>
          <div className={chipClass}>
            <Icon name="UserIcon" className={clsx('text-blue-dark/70 shrink-0', iconSize)} />
            <span className={clsx('max-w-40 truncate font-medium', textSize)}>{userLabel(u)}</span>
          </div>
        </Tooltip>
      ))}
    </div>
  );
};

export default RecipientsList;
